import React, { createContext, useContext, useState, ReactNode } from 'react';

interface LoadingStates {
  aiResponse: boolean;
  fileUpload: boolean;
  scriptExecution: boolean;
  dragDrop: boolean;
}

interface LoadingContextType {
  loadingStates: LoadingStates;
  setLoading: (key: keyof LoadingStates, value: boolean) => void;
  isAnyLoading: boolean;
  loadingMessage: string;
  canStartOperation: (operation: keyof LoadingStates) => boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    aiResponse: false,
    fileUpload: false,
    scriptExecution: false,
    dragDrop: false
  });

  const setLoading = (key: keyof LoadingStates, value: boolean) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  const isAnyLoading = Object.values(loadingStates).some(v => v);

  const getLoadingMessage = (): string => {
    if (loadingStates.aiResponse) return 'AI is thinking...';
    if (loadingStates.fileUpload) return 'Uploading files...';
    if (loadingStates.scriptExecution) return 'Executing script...';
    if (loadingStates.dragDrop) return 'Processing dropped files...';
    return 'Loading...';
  };

  // Prevent conflicting operations
  const canStartOperation = (operation: keyof LoadingStates): boolean => {
    // Don't allow file operations during AI response
    if (operation === 'fileUpload' || operation === 'dragDrop') {
      return !loadingStates.aiResponse && !loadingStates.scriptExecution;
    }
    // Don't allow AI operations during file upload
    if (operation === 'aiResponse') {
      return !loadingStates.fileUpload && !loadingStates.dragDrop;
    }
    return !isAnyLoading;
  };

  return (
    <LoadingContext.Provider value={{
      loadingStates,
      setLoading,
      isAnyLoading,
      loadingMessage: getLoadingMessage(),
      canStartOperation
    }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
};